import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';

const OVERFLOW_TOLERANCE = 1;

/**
 * Custom hook to detect if a line-clamped element is actually hiding part of its text.
 * Measures the real overflow instead of guessing from the text length, and re-measures
 * whenever the element resizes (viewport, container) or the web font swaps in and reflows it.
 *
 * Pass `enabled: false` while the element is expanded — the clamp is off then, so there is
 * nothing to measure and the last collapsed measurement is kept.
 */
export const useIsClamped = (targetRef: MutableRefObject<HTMLElement | null>, enabled: boolean = true) => {
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const element = targetRef.current;
    if (!element || !enabled) return undefined;

    const measure = () => setIsClamped(element.scrollHeight - element.clientHeight > OVERFLOW_TOLERANCE);
    measure();
    document.fonts?.ready.then(measure);

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [targetRef, enabled]);

  return isClamped;
};
